import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import i18n from '../i18n/config';

// ────────────────────────────────────────────────────────────────────────────
// Subdomain utilities for white-labeling
// ────────────────────────────────────────────────────────────────────────────

/**
 * Checks if we're on the main kovatravel.com domain (no subdomain).
 * Used to show stealth landing page on the main domain.
 */
export function isMainDomain(): boolean {
  const hostname = window.location.hostname;

  // localhost/dev always shows the app (not stealth)
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return false;
  }

  // Vercel preview URLs - show the app
  if (hostname.endsWith('.vercel.app')) {
    return false;
  }

  // Main domain: kovatravel.com or www.kovatravel.com
  if (hostname === 'kovatravel.com' || hostname === 'www.kovatravel.com') {
    return true;
  }

  return false;
}

/**
 * Extracts subdomain or custom domain from the current URL
 * - acme.kovatravel.com → 'acme'
 * - example.com → 'example.com' (custom domain, matched in DB)
 * - localhost:5173 → null (use ?subdomain=acme for dev testing)
 */
export function getSubdomain(): string | null {
  const hostname = window.location.hostname;

  // For localhost development, allow ?subdomain=acme query param for testing
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    const params = new URLSearchParams(window.location.search);
    return params.get('subdomain');
  }

  const parts = hostname.split('.');

  // Handle acme.kovatravel.com → ['acme', 'kovatravel', 'com']
  if (hostname.endsWith('.kovatravel.com') && parts.length >= 3) {
    const subdomain = parts[0];
    if (subdomain !== 'www') {
      return subdomain;
    }
  }

  // Handle acme.kovatravel.vercel.app for testing
  if (hostname.endsWith('.kovatravel.vercel.app') && parts.length >= 4) {
    const subdomain = parts[0];
    if (subdomain !== 'www') {
      return subdomain;
    }
  }

  // Handle custom domains (e.g., example.com)
  // Return full hostname to match against custom_domain column in agencies table
  if (!hostname.endsWith('.kovatravel.com') &&
      !hostname.endsWith('.vercel.app') &&
      !hostname.includes('localhost')) {
    return hostname;
  }

  return null;
}

/**
 * Fetches agency branding by subdomain (no auth required)
 */
async function fetchAgencyBySubdomain(subdomain: string) {
  try {
    const { data, error } = await supabase.rpc('get_public_agency_theme', {
      p_domain: subdomain,
    });

    if (error) return null;
    return data;
  } catch {
    return null;
  }
}

// ────────────────────────────────────────────────────────────────────────────
// Theme types and defaults
// ────────────────────────────────────────────────────────────────────────────

export interface StyleSettings {
  branding: {
    logoUrl: string | null;
    brandName: string;
  };
  colors: {
    primary: string;
    accent: string;
    accentGold: string;
    background: string;
    headingText: string;
    secondaryText: string;
    success: string;
    error: string;
    warning: string;
    info: string;
    backgroundHover: string;
    backgroundAlt: string;
    infoBg: string;
    overlay: string;
    accentPurple: string;
    accentTan: string;
    modalBackground: string;
    modalSidebar: string;
    imagePlaceholder: string;
    formBackground: string;
    borderLight: string;
    borderDefault: string;
    borderStrong: string;
    textMuted: string;
    textStrong: string;
    successLight: string;
    successDark: string;
    textColor: string;
  };
  buttons: {
    buttonColor: string;
    buttonTextColor: string;
    buttonHoverColor: string;
    buttonHoverDark: string;
    buttonDangerColor: string;
    buttonSuccessColor: string;
  };
  typography: {
    fontFamily: string;
  };
  eventColors: {
    flights: string;
    hotels: string;
    cruises: string;
    dining: string;
    activities: string;
    transportation: string;
    insurance: string;
    other: string;
  };
}

const defaultTheme: StyleSettings = {
  branding: {
    logoUrl: null,
    brandName: 'KOVA',
  },
  colors: {
    primary: '#412F21',
    accent: '#7A9E7E',
    accentGold: '#C9A961',
    background: '#FBF9F6',
    headingText: '#1A1A1A',
    secondaryText: '#6B6B6B',
    success: '#7A9E7E',
    error: '#C4785A',
    warning: '#D4AF37',
    info: '#6B9BC3',
    backgroundHover: '#F5F3EF',
    backgroundAlt: '#FAFAF8',
    infoBg: '#FFFBF3',
    overlay: '#1C110A',
    accentPurple: '#9B7EDE',
    accentTan: '#B89A6E',
    modalBackground: '#F7F4F0',
    modalSidebar: '#EDE8E2',
    imagePlaceholder: '#E2DDD7',
    formBackground: '#F0EBE4',
    borderLight: '#F0EBE4',
    borderDefault: '#D4C8BA',
    borderStrong: '#9E8A77',
    textMuted: '#9B9088',
    textStrong: '#2D1F15',
    successLight: '#5F8062',
    successDark: '#4A6349',
    textColor: '#412F21',
  },
  buttons: {
    buttonColor: '#412F21',
    buttonTextColor: '#FBF9F6',
    buttonHoverColor: '#5a4230',
    buttonHoverDark: '#2d1f15',
    buttonDangerColor: '#C4785A',
    buttonSuccessColor: '#7A9E7E',
  },
  typography: {
    fontFamily: 'Montserrat',
  },
  eventColors: {
    flights: '#6B9BC3',
    hotels: '#B89A6E',
    cruises: '#7A9E7E',
    dining: '#C4785A',
    activities: '#9B7EDE',
    transportation: '#8B7355',
    insurance: '#D4AF37',
    other: '#A0AEC0',
  },
};

interface ThemeContextType {
  theme: StyleSettings;
  setTheme: (theme: StyleSettings) => void;
  isLoading: boolean;
  refreshTheme: () => Promise<void>;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

// Helper function to convert hex to RGB
function hexToRgb(hex: string): string {
  // Remove # if present
  if (!hex) {
    return "";
  }

  hex = hex.replace("#", "");

  // Parse hex values
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  return `${r}, ${g}, ${b}`;
}

// Helper function to darken a hex color by a percentage
function darkenHex(hex: string, percent: number): string {
  if (!hex) return "#000000";

  hex = hex.replace("#", "");

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Darken by reducing each channel by the percentage
  const factor = 1 - (percent / 100);
  const newR = Math.round(r * factor);
  const newG = Math.round(g * factor);
  const newB = Math.round(b * factor);

  return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [theme, setTheme] = useState<StyleSettings>(defaultTheme);
  const [isLoading, setIsLoading] = useState(true);

  const fetchTheme = async () => {
    try {
      // Get current user's agency
      const { data: { user } } = await supabase.auth.getUser();

      // If no user logged in, check for subdomain branding
      if (!user) {
        const subdomain = getSubdomain();
        if (subdomain) {
          const agencyData = await fetchAgencyBySubdomain(subdomain);
          if (agencyData?.style_settings) {
            const subdomainTheme: StyleSettings = {
              ...defaultTheme,
              ...agencyData.style_settings,
              branding: {
                ...defaultTheme.branding,
                ...agencyData.style_settings?.branding,
                logoUrl: agencyData.logo_url || agencyData.style_settings?.branding?.logoUrl || null,
              },
            };
            setTheme(subdomainTheme);
            setIsLoading(false);
            return;
          }
        }
        setTheme(defaultTheme);
        setIsLoading(false);
        return;
      }

      // Travelers don't have profiles - use default theme
      if (user.user_metadata?.role === 'traveler') {
        setTheme(defaultTheme);
        setIsLoading(false);
        return;
      }

      // Get user's profile to find their agency and language preference (agents only)
      // Wrap in try-catch to handle cases where profile doesn't exist yet
      // (e.g., during email verification before profile is fully set up)
      let profile;
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('agency_id, preferred_language')
          .eq('id', user.id)
          .single();

        if (error) {
          // Profile doesn't exist or not accessible - use default theme
          setTheme(defaultTheme);
          setIsLoading(false);
          return;
        }
        profile = data;

        // Apply user's language preference (but not on itinerary view pages where itinerary language takes precedence)
        const isItineraryViewPage = window.location.pathname.startsWith('/itinerary/') || window.location.pathname.startsWith('/view/');
        if (profile.preferred_language && !isItineraryViewPage) {
          i18n.changeLanguage(profile.preferred_language);
          localStorage.setItem('language', profile.preferred_language);
        }
      } catch {
        // Network or other error - use default theme
        setTheme(defaultTheme);
        setIsLoading(false);
        return;
      }

      if (!profile?.agency_id) {
        setTheme(defaultTheme);
        setIsLoading(false);
        return;
      }

      // Fetch agency styles
      const { data: agency, error } = await supabase
        .from('agencies')
        .select('style_settings, logo_url')
        .eq('agency_id', profile.agency_id)
        .single();

      if (error) {
        setTheme(defaultTheme);
      } else if (agency) {
        const agencyTheme: StyleSettings = {
          ...defaultTheme,
          ...agency.style_settings,
          branding: {
            ...defaultTheme.branding,
            ...agency.style_settings?.branding,
            logoUrl: agency.logo_url || agency.style_settings?.branding?.logoUrl || null,
          },
        };
        setTheme(agencyTheme);
      }
    } catch {
      setTheme(defaultTheme);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshTheme = async () => {
    await fetchTheme();
  };

  useEffect(() => {
    fetchTheme();

    // Subscribe to auth changes to reload theme when user logs in/out
    const { data: authListener } = supabase.auth.onAuthStateChange(() => {
      fetchTheme();
    });

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, []);

  // Apply CSS variables whenever theme changes
  useEffect(() => {
    const root = document.documentElement;

    // Apply colors (hex format)
    root.style.setProperty('--theme-primary', theme.colors.primary);
    root.style.setProperty('--theme-accent', theme.colors.accent);
    root.style.setProperty('--theme-accent-gold', theme.colors.accentGold);
    root.style.setProperty('--theme-background', theme.colors.background);
    root.style.setProperty('--theme-heading-text', theme.colors.headingText);
    root.style.setProperty('--theme-secondary-text', theme.colors.secondaryText);
    root.style.setProperty('--theme-success', theme.colors.success);
    root.style.setProperty('--theme-error', theme.colors.error);
    root.style.setProperty('--theme-warning', theme.colors.warning);
    root.style.setProperty('--theme-info', theme.colors.info);
    root.style.setProperty('--theme-background-hover', theme.colors.backgroundHover);
    root.style.setProperty('--theme-background-alt', theme.colors.backgroundAlt);
    root.style.setProperty('--theme-info-bg', theme.colors.infoBg);
    root.style.setProperty('--theme-overlay', theme.colors.overlay);
    root.style.setProperty('--theme-accent-purple', theme.colors.accentPurple);
    root.style.setProperty('--theme-accent-tan', theme.colors.accentTan);
    root.style.setProperty('--theme-modal-background', theme.colors.modalBackground);
    root.style.setProperty('--theme-modal-sidebar', theme.colors.modalSidebar);
    root.style.setProperty('--theme-image-placeholder', theme.colors.imagePlaceholder);
    root.style.setProperty('--theme-form-background', theme.colors.formBackground);
    root.style.setProperty('--theme-border-light', theme.colors.borderLight);
    root.style.setProperty('--theme-border-default', theme.colors.borderDefault);
    root.style.setProperty('--theme-border-strong', theme.colors.borderStrong);
    root.style.setProperty('--theme-text-muted', theme.colors.textMuted);
    root.style.setProperty('--theme-text-strong', theme.colors.textStrong);
    root.style.setProperty('--theme-success-light', theme.colors.successLight);
    root.style.setProperty('--theme-success-dark', theme.colors.successDark);
    root.style.setProperty('--theme-button-bg', theme.buttons.buttonColor);
    root.style.setProperty('--theme-button-text', theme.buttons.buttonTextColor);
    root.style.setProperty('--theme-button-hover', theme.buttons.buttonHoverColor);
    root.style.setProperty('--theme-button-hover-dark', theme.buttons.buttonHoverDark);
    root.style.setProperty('--theme-button-danger', theme.buttons.buttonDangerColor);
    root.style.setProperty('--theme-button-success', theme.buttons.buttonSuccessColor);

    // Fixed overlay color (theme-independent for consistency)
    root.style.setProperty('--overlay-modal', 'rgba(30, 30, 30, 0.35)');

    root.style.setProperty('--theme-text', theme.colors.textColor);
    root.style.setProperty('--theme-text-rgb', hexToRgb(theme.colors.textColor));

    // Apply colors (RGB format for use with rgba())
    root.style.setProperty('--theme-primary-rgb', hexToRgb(theme.colors.primary));
    root.style.setProperty('--theme-accent-rgb', hexToRgb(theme.colors.accent));
    root.style.setProperty('--theme-accent-gold-rgb', hexToRgb(theme.colors.accentGold));
    root.style.setProperty('--theme-background-rgb', hexToRgb(theme.colors.background));
    root.style.setProperty('--theme-heading-text-rgb', hexToRgb(theme.colors.headingText));
    root.style.setProperty('--theme-secondary-text-rgb', hexToRgb(theme.colors.secondaryText));
    root.style.setProperty('--theme-success-rgb', hexToRgb(theme.colors.success));
    root.style.setProperty('--theme-error-rgb', hexToRgb(theme.colors.error));
    root.style.setProperty('--theme-warning-rgb', hexToRgb(theme.colors.warning));
    root.style.setProperty('--theme-info-rgb', hexToRgb(theme.colors.info));
    root.style.setProperty('--theme-background-hover-rgb', hexToRgb(theme.colors.backgroundHover));
    root.style.setProperty('--theme-background-alt-rgb', hexToRgb(theme.colors.backgroundAlt));
    root.style.setProperty('--theme-info-bg-rgb', hexToRgb(theme.colors.infoBg));
    root.style.setProperty('--theme-overlay-rgb', hexToRgb(theme.colors.overlay));
    root.style.setProperty('--theme-accent-purple-rgb', hexToRgb(theme.colors.accentPurple));
    root.style.setProperty('--theme-accent-tan-rgb', hexToRgb(theme.colors.accentTan));
    root.style.setProperty('--theme-modal-background-rgb', hexToRgb(theme.colors.modalBackground));
    root.style.setProperty('--theme-modal-sidebar-rgb', hexToRgb(theme.colors.modalSidebar));
    root.style.setProperty('--theme-image-placeholder-rgb', hexToRgb(theme.colors.imagePlaceholder));
    root.style.setProperty('--theme-form-background-rgb', hexToRgb(theme.colors.formBackground));
    root.style.setProperty('--theme-border-light-rgb', hexToRgb(theme.colors.borderLight));
    root.style.setProperty('--theme-border-default-rgb', hexToRgb(theme.colors.borderDefault));
    root.style.setProperty('--theme-border-strong-rgb', hexToRgb(theme.colors.borderStrong));
    root.style.setProperty('--theme-text-muted-rgb', hexToRgb(theme.colors.textMuted));
    root.style.setProperty('--theme-text-strong-rgb', hexToRgb(theme.colors.textStrong));
    root.style.setProperty('--theme-success-light-rgb', hexToRgb(theme.colors.successLight));
    root.style.setProperty('--theme-success-dark-rgb', hexToRgb(theme.colors.successDark));
    root.style.setProperty('--theme-button-bg-rgb', hexToRgb(theme.buttons.buttonColor));
    root.style.setProperty('--theme-button-text-rgb', hexToRgb(theme.buttons.buttonTextColor));
    root.style.setProperty('--theme-button-hover-rgb', hexToRgb(theme.buttons.buttonHoverColor));
    root.style.setProperty('--theme-button-hover-dark-rgb', hexToRgb(theme.buttons.buttonHoverDark));
    root.style.setProperty('--theme-button-danger-rgb', hexToRgb(theme.buttons.buttonDangerColor));
    root.style.setProperty('--theme-button-success-rgb', hexToRgb(theme.buttons.buttonSuccessColor));

    // Apply event colors
    root.style.setProperty('--theme-event-flights', theme.eventColors.flights);
    root.style.setProperty('--theme-event-hotels', theme.eventColors.hotels);
    root.style.setProperty('--theme-event-cruises', theme.eventColors.cruises);
    root.style.setProperty('--theme-event-dining', theme.eventColors.dining);
    root.style.setProperty('--theme-event-activities', theme.eventColors.activities);
    root.style.setProperty('--theme-event-transportation', theme.eventColors.transportation);
    root.style.setProperty('--theme-event-insurance', theme.eventColors.insurance);
    root.style.setProperty('--theme-event-other', theme.eventColors.other);

    // Apply font family
    root.style.setProperty('--theme-font-family', theme.typography.fontFamily);
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, isLoading, refreshTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};
